'use client';

import { useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { createQaAnswer } from '#/actions/server/qa';
import { Button } from '#/components/ui/button';
import RichTextEditor from '#/components/editor/rich-text-editor';
import { Loader2, Send, LogIn } from 'lucide-react';

interface QaAnswerFormProps {
  questionId: string;
  userId: string | null;
  locked?: boolean;
}

export default function QaAnswerForm({
  questionId,
  userId,
  locked = false
}: QaAnswerFormProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const stripHtml = (html: string) =>
    html
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const plainTextLength = stripHtml(text).length;

  const handleSubmit = () => {
    if (!stripHtml(text)) {
      setError('답변 내용을 입력해주세요.');
      return;
    }
    if (plainTextLength < 10) {
      setError('답변은 최소 10자 이상 작성해주세요.');
      return;
    }

    setError('');
    startTransition(async () => {
      try {
        await createQaAnswer({
          questionId,
          text: text.trim()
        });
        setText('');
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        router.refresh();
      } catch (err: any) {
        setError(err.message || '답변 등록에 실패했습니다.');
      }
    });
  };

  if (locked) {
    return (
      <div className="rounded-2xl border border-[#104573]/10 bg-[#104573]/[0.03] p-6 text-center">
        <p className="text-muted-foreground text-sm">
          🔒 이 질문은 잠겨 있어 답변을 작성할 수 없습니다.
        </p>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="rounded-2xl border border-[#104573]/10 bg-[#104573]/[0.03] p-6 text-center">
        <p className="text-muted-foreground mb-3">
          답변을 작성하려면 로그인이 필요합니다.
        </p>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => router.push(`/login?callbackUrl=/qa/${questionId}`)}
        >
          <LogIn className="w-4 h-4" />
          로그인하기
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#104573]/10 bg-card p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">답변 작성</h3>

      <div className="mb-3">
        <RichTextEditor
          content={text}
          onChange={(html) => {
            setText(html);
            if (error) setError('');
          }}
          placeholder="이 질문에 대한 답변을 작성해주세요. 정확한 출처나 근거를 함께 제시하면 더 도움이 됩니다."
          minHeight="180px"
        />
      </div>

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-3 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
          ✅ 답변이 성공적으로 등록되었습니다!
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {plainTextLength}자{' '}
          {plainTextLength < 10 && plainTextLength > 0 && '(최소 10자)'}
        </p>
        <Button
          onClick={handleSubmit}
          disabled={isPending || !stripHtml(text)}
          className="gap-2"
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              등록 중...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              답변 등록
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
